import { useEffect, useRef } from "react";
import { observer } from "mobx-react";
import TextField from "@mui/material/TextField";

import { QuizObject } from "../../../../../store/modquiz/quiz_objects/quiz-object";

import { toJS } from 'mobx';

import EditorJS from "@editorjs/editorjs";
import Table from "@editorjs/table";
import List from "@editorjs/list";
import Image from "@editorjs/image";
import Header from "@editorjs/header";

export const EDITOR_JS_TOOLS = {
  table: Table,
  list: List,
  image: Image,
  header: Header,
};

interface Props {
    quiz_object: QuizObject;
    handleTitleChange: (ev?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onSave: () => void;
}

const AUTOSAVE_DELAY_MS = 3000;

const FullObjectPane = observer(({ quiz_object, handleTitleChange, onSave }: Props) => {
    const holderRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<EditorJS | null>(null);
    const generationRef = useRef(0);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;

    const scheduleSave = () => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => onSaveRef.current(), AUTOSAVE_DELAY_MS);
    };

    const blocks = [
        ...quiz_object.card_tables.map(card_table => ({
            type: "table",
            data: {
                content: card_table.cards.map(card => [
                    `<a href=${card.link}><span class="lnr-small lnr lnr-link"></span></a>`,
                    card.title,
                    card.definition,
                ]),
            },
        })),
        ...quiz_object.chunks.map(chunk => ({
            type: "paragraph",
            data: { text: chunk.content },
        })),
        ...quiz_object.lists.map(list => ({
            type: "list",
            data: {
                style: list.ordered ? "ordered" : "unordered",
                items: toJS(list.list),
            },
        })),
        ...quiz_object.tables.map(table => ({
            type: "table",
            data: { content: table.Rows ?? [] },
        })),
    ];

    useEffect(() => {
        if (!holderRef.current) return;

        const myGeneration = ++generationRef.current;

        const editor = new EditorJS({
            holder: holderRef.current,
            data: { blocks },
            tools: EDITOR_JS_TOOLS,
            placeholder: "Start writing...",
            onChange: () => scheduleSave(),
        } as any);

        editorRef.current = editor;

        return () => {
            editorRef.current = null;
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            editor.isReady.then(() => {
                if (generationRef.current === myGeneration) {
                    editor.destroy();
                }
            }).catch(() => {});
        };
    }, []);

    return (
        <div className="QuizObjectEditor">
            <TextField
                fullWidth
                variant="standard"
                label="Title"
                value={quiz_object.title}
                onChange={(e) => { handleTitleChange(e); scheduleSave(); }}
                style={{ marginBottom: "1rem" }}
            />
            <div
                ref={holderRef}
                style={{ minHeight: "200px", border: "1px solid #eee", padding: "8px" }}
            />
        </div>
    );
});

export default FullObjectPane;
